import { buildHandoffUrl } from './utils';
import type { RecoveryBook, RecoveryCandidate, RecoveryDoctor } from './types';

/**
 * The chat-recovery state machine — pure, no side effects, no app imports, so the
 * transitions are testable in isolation (and the hook stays a thin wrapper).
 */
export type RecoveryPhase =
  | 'idle' // not fetching / nothing to show
  | 'offered' // dock visible
  | 'confirming' // sheet visible
  | 'cancelling' // cancel in flight
  | 'waiting_refund' // cancel done, money not yet visible in wallet
  | 'done' // handed off to the booking page — our job is finished
  | 'failed' // cancel failed — dock stays usable
  | 'unavailable'; // server says none/voided, fetch failed, or dismissed

export interface RecoveryContextData {
  bookId?: string;
  doctor?: RecoveryDoctor;
  candidate?: RecoveryCandidate;
  minutesOverdue?: number;
  /** Deep link to the replacement booking page, null when the payload was malformed. */
  handoffUrl: string | null;
}

export type RecoveryAction =
  | { type: 'FETCH_EMPTY' }
  | { type: 'FETCH_OK'; book: RecoveryBook; doctor: RecoveryDoctor; candidate: RecoveryCandidate; minutesOverdue?: number }
  | { type: 'OPEN_CONFIRM' }
  | { type: 'CONFIRM_DISMISS' }
  | { type: 'CANCEL_OK' }
  | { type: 'CANCEL_FAIL' }
  | { type: 'REFUND_OK' }
  | { type: 'REFUND_SLOW' }
  | { type: 'DONE' }
  | { type: 'FAIL_BACK' }
  | { type: 'DISMISS' }
  | { type: 'RESET' };

export interface RecoveryState {
  phase: RecoveryPhase;
  data: RecoveryContextData;
}

export const initialRecoveryState: RecoveryState = { phase: 'idle', data: { handoffUrl: null } };

export function recoveryReducer(state: RecoveryState, action: RecoveryAction): RecoveryState {
  switch (action.type) {
    case 'RESET':
      // تعویض چت: stateِ چت قبلی هرگز روی چت جدید رندر نمی‌شود (ریویو ۲۰۲۶-۱۰-۱۱)
      return initialRecoveryState;
    case 'FETCH_EMPTY':
      // A void after offering must not yank the card from under the patient —
      // only a fresh fetch (idle) resolves to unavailable.
      return state.phase === 'idle' ? { ...state, phase: 'unavailable' } : state;
    case 'FETCH_OK':
      return {
        phase: 'offered',
        data: {
          bookId: action.book.book_id,
          doctor: action.doctor,
          candidate: action.candidate,
          minutesOverdue: action.minutesOverdue,
          handoffUrl: buildHandoffUrl(action.candidate),
        },
      };
    case 'OPEN_CONFIRM':
      return state.phase === 'offered' ? { ...state, phase: 'confirming' } : state;
    case 'CONFIRM_DISMISS':
      return state.phase === 'confirming' ? { ...state, phase: 'offered' } : state;
    case 'CANCEL_OK':
      return { ...state, phase: 'waiting_refund' };
    case 'CANCEL_FAIL':
      return { ...state, phase: 'failed' };
    case 'REFUND_OK':
    case 'REFUND_SLOW':
      // Slow katibe read still completes — the money is safe either way.
      return { ...state, phase: 'done' };
    case 'DONE':
      return { ...state, phase: 'done' };
    case 'FAIL_BACK':
      return { ...state, phase: 'offered' };
    case 'DISMISS':
      return { ...state, phase: 'unavailable' };
    default:
      return state;
  }
}
